import React, { useMemo, useState } from 'react'
import { useThemeMediaQuery } from 'hooks/useThemeMediaQuery'
import { License, isLastItem } from '@mprolab/sdk'
import { Flex, Text } from '@mprolab/uikit'
import { Grid } from '@mui/material'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp } from 'react-feather'
import IndexNodeRow from './IndexNodeRow'
import { sortLicences, type SortDirection, type SortKey } from './sortLicences'
import { useActiveListingsForLicences } from 'hooks/useActiveListingsForLicences'

const StyledColumnText = styled(Text)`
  color: ${({ theme }) => theme.colors.gray6};
`

const SortableHeader = styled(Flex)<{ active: boolean }>`
  gap: 4px;
  align-items: center;
  cursor: pointer;
  user-select: none;
  color: ${({ theme, active }) => (active ? theme.colors.white : theme.colors.gray6)};
  &:hover {
    color: ${({ theme }) => theme.colors.white};
  }
`

interface IndexNodesTableProps {
  licenses: License[]
  refetchLicenses: () => void
}

const IndexNodesTable: React.FC<IndexNodesTableProps> = ({ licenses, refetchLicenses }) => {
  const { t } = useTranslation()
  const isMobile = useThemeMediaQuery('mobile')
  // Opens on the order the server already returns, so the first view is the
  // one the list is documented to have.
  const [sortKey, setSortKey] = useState<SortKey>('name')
  const [direction, setDirection] = useState<SortDirection>('asc')
  // Which of these are on the marketplace right now, read from the contract.
  const activeListings = useActiveListingsForLicences(licenses)
  const listedTokenIds = useMemo(
    () => new Set(Object.keys(activeListings)),
    [activeListings]
  )

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortKey(key)
    setDirection('asc')
  }

  const sorted = useMemo(
    () => sortLicences(licenses, sortKey, direction, listedTokenIds),
    [licenses, sortKey, direction, listedTokenIds]
  )

  const header = (key: SortKey, label: string) => (
    <SortableHeader active={sortKey === key} onClick={() => toggleSort(key)}>
      <StyledColumnText style={{ color: 'inherit' }}>{label}</StyledColumnText>
      {sortKey === key &&
        (direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
    </SortableHeader>
  )

  return (
    <Flex flexDirection="column" height="100%">
      {!isMobile && (
        <Grid container mb="8px">
          <Grid item xs={2}>
            {header('id', t('licenses.systemId'))}
          </Grid>
          <Grid item xs={2.2}>
            {header('name', t('licenses.node'))}
          </Grid>
          <Grid item xs={1.1}>
            {header('type', t('licenses.type'))}
          </Grid>
          <Grid item xs={1.4}>
            {header('created', t('licenses.createdDate'))}
          </Grid>
          <Grid item xs={1.4}>
            {header('acquired', t('licenses.purchaseDate'))}
          </Grid>
          <Grid item xs={1.3}>
            {header('listed', t('licenses.saleStatus'))}
          </Grid>
          {/*
            Not sortable, unlike its neighbours. Sorting by a dot that changes
            on its own every fifteen minutes would reorder the table under
            somebody's cursor, and the question this column answers — "which of
            mine is running?" — is answered by scanning eight rows, not by
            sorting them.
          */}
          <Grid item xs={1.1}>
            <StyledColumnText>Terminal</StyledColumnText>
          </Grid>
          <Grid item xs={1} justifyContent="flex-end" display="flex" />
        </Grid>
      )}
      {sorted.map((license, index) => {
        const isLast = isLastItem(sorted, index)
        return (
          // Keyed by the licence, never by position. A row holds its own state
          // — the on/off toggle is seeded once from the licence it mounted
          // with — so keying by index hands that state to whichever licence
          // later occupies the slot, and sorting changes those slots on every
          // click.
          <IndexNodeRow
            key={license.licenseId}
            {...{ license, licenses, isLast, refetchLicenses }}
            listingId={
              license.tokenId ? activeListings[String(license.tokenId)] : undefined
            }
          />
        )
      })}
    </Flex>
  )
}

export default IndexNodesTable
